/**
 * LiveKit device selection utilities.
 */
import { Room, createLocalAudioTrack, createLocalVideoTrack } from 'livekit-client';
import type { DeviceSelection } from './livekitClientTypes';
import { logError } from '../logging/logger';
import { WEB_CAMERA_CAPTURE_OPTIONS } from './mediaQuality';

/**
 * Checks if audio output device selection is supported.
 * setSinkId is not available on mobile browsers.
 */
function isAudioOutputSupported(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

/**
 * Applies device selection to the room.
 */
export async function applyDeviceSelection(
  room: Room | null,
  selection: DeviceSelection
): Promise<void> {
  if (!room) {
    return;
  }

  if (!room.localParticipant) {
    return;
  }

  if (selection.microphoneDeviceId) {
    await room.switchActiveDevice('audioinput', selection.microphoneDeviceId);
  }

  if (selection.cameraDeviceId) {
    await room.switchActiveDevice('videoinput', selection.cameraDeviceId);
  }

  if (selection.speakerDeviceId && isAudioOutputSupported()) {
    await room.switchActiveDevice('audiooutput', selection.speakerDeviceId);
  }
}

/**
 * Gets available microphones.
 */
export async function getAvailableMicrophones(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'audioinput');
}

/**
 * Gets available cameras.
 */
export async function getAvailableCameras(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'videoinput');
}

/**
 * Gets available speakers.
 */
export async function getAvailableSpeakers(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'audiooutput');
}

/**
 * Creates local tracks with device preferences.
 */
export async function createLocalTracks(
  audioEnabled: boolean,
  videoEnabled: boolean,
  microphoneDeviceId?: string,
  cameraDeviceId?: string
): Promise<{
  audioTrack?: Awaited<ReturnType<typeof createLocalAudioTrack>>;
  videoTrack?: Awaited<ReturnType<typeof createLocalVideoTrack>>;
}> {
  const result: {
    audioTrack?: Awaited<ReturnType<typeof createLocalAudioTrack>>;
    videoTrack?: Awaited<ReturnType<typeof createLocalVideoTrack>>;
  } = {};

  if (audioEnabled) {
    try {
      result.audioTrack = await createLocalAudioTrack({
        deviceId: microphoneDeviceId,
      });
    } catch (error) {
      logError('Failed to create audio track.', error);
    }
  }

  if (videoEnabled) {
    try {
      result.videoTrack = await createLocalVideoTrack({
        deviceId: cameraDeviceId,
        ...WEB_CAMERA_CAPTURE_OPTIONS,
      });
    } catch (error) {
      logError('Failed to create video track.', error);
    }
  }

  return result;
}
